'use client'

// ============================================================
// CookieBanner - LGPD opt-in granular
// ============================================================
// - Aparece quando nao ha cookie ebuz_consent OU policy_version mudou
// - 2 modos: simples (Aceitar Todos / Personalizar) e detailed (4 toggles)
// - Default LGPD: essential=true (locked), demais=false
// - Salva cookie ebuz_consent (1 ano) + chama POST /api/lgpd/consent
// - Nao bloqueia rendering: aparece apos hydration
// ============================================================

import { useEffect, useState } from 'react'

const COOKIE_NAME = 'ebuz_consent'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 ano em segundos
const SESSION_COOKIE_NAME = 'ebuz_session_id'

type ConsentCategory = 'essential' | 'analytics' | 'marketing' | 'personalization'
type ConsentMap = Record<ConsentCategory, boolean>

interface ConsentCookie {
  version: string
  categories: ConsentMap
  acceptedAt: string
}

interface CategoryDefinition {
  required: boolean
  description: string
}

interface PolicyDefinition {
  version: string
  categories: Record<ConsentCategory, CategoryDefinition>
}

const DEFAULT_CATEGORIES: PolicyDefinition['categories'] = {
  essential: {
    required: true,
    description:
      'Cookies essenciais: necessarios para login, seguranca e funcionamento basico do site. Nao podem ser desativados.',
  },
  analytics: {
    required: false,
    description:
      'Cookies de analytics: ajudam a entender como voce usa o site (Google Analytics) para melhorar a experiencia.',
  },
  marketing: {
    required: false,
    description:
      'Cookies de marketing: usados para mostrar anuncios relevantes (Meta Pixel, Google Ads).',
  },
  personalization: {
    required: false,
    description:
      'Cookies de personalizacao: lembram suas preferencias para personalizar conteudo e recomendacoes.',
  },
}

// ------------------------------------------------------------
// Helpers de cookie (client-side)
// ------------------------------------------------------------
function readConsentCookie(): ConsentCookie | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`))
  if (!match) return null
  try {
    const value = decodeURIComponent(match.split('=')[1])
    return JSON.parse(value) as ConsentCookie
  } catch {
    return null
  }
}

function writeConsentCookie(consent: ConsentCookie) {
  if (typeof document === 'undefined') return
  const value = encodeURIComponent(JSON.stringify(consent))
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${COOKIE_NAME}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`
}

function ensureSessionId(): string {
  if (typeof document === 'undefined') return ''
  const existing = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${SESSION_COOKIE_NAME}=`))
  if (existing) {
    return existing.split('=')[1]
  }
  // Gera ID anonimo (UUID v4 simplificado)
  const id = crypto.randomUUID()
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${SESSION_COOKIE_NAME}=${id}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`
  return id
}

// ------------------------------------------------------------
// i18n do banner. O texto era 100% PT hardcoded, entao o iberoconnect.com,
// que e um site em espanhol para publico na Espanha, pedia consentimento de
// cookies em portugues — justamente a tela que precisa ser compreendida.
// Default pt: todos os outros tenants continuam identicos.
// ------------------------------------------------------------
const BANNER_STRINGS = {
  pt: {
    title: 'Sua privacidade importa',
    lead: 'Usamos cookies essenciais para o site funcionar. Também queremos usar cookies opcionais para analytics, marketing e personalização. Você decide. Veja nossa ',
    privacyLink: 'Política de Privacidade',
    customize: 'Personalizar',
    rejectOptional: 'Rejeitar opcionais',
    acceptAll: 'Aceitar todos',
    acceptSelected: 'Aceitar selecionados',
    saving: 'Salvando...',
    back: 'Voltar',
    detailTitle: 'Personalize seus consentimentos',
    detailLead: 'Escolha quais categorias de cookies você permite. Cookies essenciais são',
    catEssential: 'Essenciais',
    catAnalytics: 'Analytics',
    catMarketing: 'Marketing',
    catPersonalization: 'Personalização',
    privacyHref: '/privacidade',
  },
  es: {
    title: 'Tu privacidad importa',
    lead: 'Usamos cookies esenciales para que el sitio funcione. También queremos usar cookies opcionales de analítica, marketing y personalización. Tú decides. Consulta nuestra ',
    privacyLink: 'Política de Privacidad',
    customize: 'Personalizar',
    rejectOptional: 'Rechazar opcionales',
    acceptAll: 'Aceptar todas',
    acceptSelected: 'Aceptar seleccionadas',
    saving: 'Guardando...',
    back: 'Volver',
    detailTitle: 'Personaliza tus consentimientos',
    detailLead: 'Elige qué categorías de cookies permites. Las cookies esenciales son',
    catEssential: 'Esenciales',
    catAnalytics: 'Analítica',
    catMarketing: 'Marketing',
    catPersonalization: 'Personalización',
    privacyHref: '/privacidade',
  },
} as const

const CATEGORIES_ES: PolicyDefinition['categories'] = {
  essential: {
    required: true,
    description:
      'Cookies esenciales: necesarias para el inicio de sesión, la seguridad y el funcionamiento básico del sitio. No se pueden desactivar.',
  },
  analytics: {
    required: false,
    description:
      'Cookies de analítica: ayudan a entender cómo usas el sitio (Google Analytics) para mejorar la experiencia.',
  },
  marketing: {
    required: false,
    description:
      'Cookies de marketing: se usan para mostrar anuncios relevantes (Meta Pixel, Google Ads).',
  },
  personalization: {
    required: false,
    description:
      'Cookies de personalización: recuerdan tus preferencias para personalizar contenido y recomendaciones.',
  },
}

// ------------------------------------------------------------
// Componente principal
// ------------------------------------------------------------
interface CookieBannerProps {
  policyVersion?: string
  categories?: PolicyDefinition['categories']
  /** BCP-47 do tenant (ex.: 'es-ES'). So o prefixo importa. Default pt. */
  locale?: string
}

export function CookieBanner({
  policyVersion = 'v1.0',
  categories,
  locale,
}: CookieBannerProps) {
  const _lang = (locale || 'pt').slice(0, 2).toLowerCase() === 'es' ? 'es' : 'pt'
  const t = BANNER_STRINGS[_lang]
  const _categories = categories ?? (_lang === 'es' ? CATEGORIES_ES : DEFAULT_CATEGORIES)
  const [mounted, setMounted] = useState(false)
  const [show, setShow] = useState(false)
  const [detailed, setDetailed] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Estado dos toggles. Essential SEMPRE true (locked).
  const [selection, setSelection] = useState<ConsentMap>({
    essential: true,
    analytics: false,
    marketing: false,
    personalization: false,
  })

  // Mount: verifica se ja consentiu na versao atual
  useEffect(() => {
    setMounted(true)
    const existing = readConsentCookie()
    if (!existing || existing.version !== policyVersion) {
      setShow(true)
    }
  }, [policyVersion])

  if (!mounted || !show) return null

  function handleToggle(category: ConsentCategory, value: boolean) {
    if (category === 'essential') return // locked
    setSelection((prev) => ({ ...prev, [category]: value }))
  }

  async function persist(consent: ConsentMap) {
    const sessionId = ensureSessionId()
    const cookie: ConsentCookie = {
      version: policyVersion,
      categories: consent,
      acceptedAt: new Date().toISOString(),
    }
    writeConsentCookie(cookie)

    // POST pra API (server registra com IP/user-agent + grava no banco).
    // Falha silenciosa: cookie ja foi salvo, banner nao reaparece.
    try {
      await fetch('/api/lgpd/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categories: consent,
          policyVersion,
          sessionId,
        }),
      })
    } catch (err) {
      // Nao bloqueia experiencia se API falhar
      console.warn('[CookieBanner] failed to POST consent:', err)
    }
  }

  async function handleAcceptAll() {
    setSubmitting(true)
    const all: ConsentMap = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true,
    }
    await persist(all)
    setShow(false)
    // Notifica scripts terceiros que podem ler o cookie
    window.dispatchEvent(new CustomEvent('ebuz:consent-changed', { detail: all }))
    setSubmitting(false)
  }

  async function handleAcceptSelected() {
    setSubmitting(true)
    await persist(selection)
    setShow(false)
    window.dispatchEvent(new CustomEvent('ebuz:consent-changed', { detail: selection }))
    setSubmitting(false)
  }

  async function handleRejectAll() {
    setSubmitting(true)
    const onlyEssential: ConsentMap = {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false,
    }
    await persist(onlyEssential)
    setShow(false)
    window.dispatchEvent(new CustomEvent('ebuz:consent-changed', { detail: onlyEssential }))
    setSubmitting(false)
  }

  return (
    <div
      role="dialog"
      aria-label="Aviso de cookies"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-ink-7 bg-paper shadow-soft"
    >
      <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6 sm:py-6">
        {!detailed ? (
          // Modo simples
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1">
              <h2 className="text-base font-semibold text-ink-0">
                {t.title}
              </h2>
              <p className="mt-1 text-sm text-ink-3">
                {t.lead}
                <a
                  href={t.privacyHref}
                  className="font-medium text-brand-600 underline hover:text-brand-700"
                >
                  {t.privacyLink}
                </a>
                .
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <button
                type="button"
                onClick={() => setDetailed(true)}
                disabled={submitting}
                className="rounded-full border border-ink-6 px-4 py-2.5 text-sm font-medium text-ink-2 hover:bg-ink-9 disabled:opacity-50"
              >
                {t.customize}
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                disabled={submitting}
                className="rounded-full border border-ink-6 px-4 py-2.5 text-sm font-medium text-ink-2 hover:bg-ink-9 disabled:opacity-50"
              >
                {t.rejectOptional}
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                disabled={submitting}
                className="rounded-full bg-brand-grad px-4 py-2.5 text-sm font-medium text-white shadow-glow hover:opacity-95 disabled:opacity-50"
              >
                {submitting ? t.saving : t.acceptAll}
              </button>
            </div>
          </div>
        ) : (
          // Modo detalhado (4 toggles)
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="text-base font-semibold text-ink-0">
                {t.detailTitle}
              </h2>
              <p className="mt-1 text-sm text-ink-3">
                {t.detailLead}{' '}
                {_lang === 'es'
                  ? 'obligatorias para que el sitio funcione.'
                  : 'obrigatorios para o site funcionar.'}
              </p>
            </div>

            <div className="space-y-3">
              {(Object.keys(_categories) as ConsentCategory[]).map((cat) => {
                const def = _categories[cat]
                const isEssential = cat === 'essential'
                return (
                  <div
                    key={cat}
                    className="flex items-start justify-between gap-4 rounded-lg border border-ink-7 p-3"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium capitalize text-ink-0">
                        {labelFor(cat, _lang)}
                        {isEssential && (
                          <span className="ml-2 rounded bg-ink-8 px-1.5 py-0.5 text-xs font-normal text-ink-3">
                            obrigatorio
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-ink-3">{def.description}</p>
                    </div>
                    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={selection[cat]}
                        onChange={(e) => handleToggle(cat, e.target.checked)}
                        disabled={isEssential}
                        aria-label={`Toggle ${labelFor(cat, _lang)}`}
                      />
                      <div className="peer h-6 w-11 rounded-full bg-ink-7 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-ink-6 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-disabled:cursor-not-allowed peer-disabled:opacity-50"></div>
                    </label>
                  </div>
                )
              })}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
              <button
                type="button"
                onClick={() => setDetailed(false)}
                disabled={submitting}
                className="rounded-full border border-ink-6 px-4 py-2.5 text-sm font-medium text-ink-2 hover:bg-ink-9 disabled:opacity-50"
              >
                {t.back}
              </button>
              <button
                type="button"
                onClick={handleAcceptSelected}
                disabled={submitting}
                className="rounded-full border border-ink-6 px-4 py-2.5 text-sm font-medium text-ink-2 hover:bg-ink-9 disabled:opacity-50"
              >
                {submitting ? t.saving : t.acceptSelected}
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                disabled={submitting}
                className="rounded-full bg-brand-grad px-4 py-2.5 text-sm font-medium text-white shadow-glow hover:opacity-95 disabled:opacity-50"
              >
                Aceitar todos
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function labelFor(category: ConsentCategory, lang: 'pt' | 'es' = 'pt'): string {
  const s = BANNER_STRINGS[lang]
  switch (category) {
    case 'essential':
      return s.catEssential
    case 'analytics':
      return s.catAnalytics
    case 'marketing':
      return s.catMarketing
    case 'personalization':
      return s.catPersonalization
  }
}
